"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WsReconnectingClient = void 0;
const isomorphic_ws_1 = __importDefault(require("isomorphic-ws"));
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const fast_safe_stringify_1 = __importDefault(require("fast-safe-stringify"));
const reconnecting_websocket_1 = require("./internal/reconnecting-websocket");
const pending_message_tracker_1 = __importDefault(require("./internal/pending-message-tracker"));
const interfaces_1 = require("./interfaces");
/**
 * Reconnecting websocket client, mainly intended for use in browser when not using @wranggle/rpc.
 *
 * Not ready for server-to-server use, at least not with WranggleRpc (See notes on the subject in WsRpcClient).
 */
class WsReconnectingClient {
    constructor(args) {
        this._connectionState = interfaces_1.WsConnectionState.Disconnected;
        this._connStateObserver = new eventemitter3_1.default();
        const { requestAuthenticatedWsUrl, url, onWsConnectionStateChange, backoffOpts, onMessage, pingMs } = args;
        this._onMessage = onMessage;
        this._pendingMessageTracker = new pending_message_tracker_1.default();
        if (onWsConnectionStateChange) {
            this.connStateObserver().on(interfaces_1.ConnectionStateChangeEvent, onWsConnectionStateChange);
        }
        this._reconnectingWebsocket = reconnecting_websocket_1._createWebsocketConnection({
            url, requestAuthenticatedWsUrl,
            onConnectionAttempt: (ws) => this._updateConnState(interfaces_1.WsConnectionState.AttemptingNow),
            backoffOpts,
            pingMs,
            // pass along override for webSocketFactory?
        });
        this._setupWsListeners();
    }
    connectionState() {
        return this._connectionState;
    }
    connStateObserver() {
        return this._connStateObserver;
    }
    /**
     * Asks WsTopicServer to subscribe to topics.
     * A list of topics can be provided, but often it will be a lookup token for fetching approved topics, or data to do so,
     *  it is passed to the depending on how the WsTopicServer is configured (using joinTopicsHandler / JoinTopicsCommand.)
     *
     * @param topicsOrTokenOrJoinData
     */
    joinTopics(topicsOrTokenOrJoinData) {
        this._sendMessage({
            [InboundTopicsCommand.JoinTopics]: topicsOrTokenOrJoinData,
        });
    }
    /**
     * Tells WsTopicServer to unsubscribe from the specified topic(s).
     * @param topics
     */
    leaveTopics(topics) {
        topics = typeof topics === 'string' ? [topics] : topics;
        this._sendMessage({
            [InboundTopicsCommand.LeaveTopics]: topics,
        });
    }
    /**
     * Sends a message over websocket connection.
     *
     * note: if connecting to WsTopicServer, this is only used to join and leave topics. Intended use pattern is to make http requests
     *   to some api with mutation commands and then only receive data over websockets.
     *
     * NOTE: prefixing with underscore to signal direct use is discouraged. It is used directly in WsRpcClient, but once in code not normally modified.
     */
    _sendMessage(data, opts = {}) {
        if (this.getRawWebSocket().readyState === isomorphic_ws_1.default.OPEN) {
            const ws = this._reconnectingWebsocket.getCurrentWebSocket();
            const msg = typeof data === 'string' ? data : fast_safe_stringify_1.default(data);
            ws.send(msg);
        }
        else { // note: sent on next connect event, for messages that pass ttl filter
            // later can perhaps add default ttl; and/or custom filters
            this._pendingMessageTracker.addMessageForLater(data, opts.ttlIfDisconnected, opts.onExpiration);
        }
        // does not handle edge case: of ws disconnecting while message is sending
    }
    disconnect() {
        this._reconnectingWebsocket.disconnect();
    }
    reconnect() {
        this._reconnectingWebsocket.reconnect();
    }
    isAutoReconnectEnabled() {
        return this._reconnectingWebsocket.isAutoReconnectEnabled();
    }
    getRawWebSocket() {
        return this._reconnectingWebsocket.getCurrentWebSocket();
    }
    /**
     * note: not filtered, some/all might not be sent
     */
    getPendingMessageCount() {
        return this._pendingMessageTracker.count();
    }
    addListener(type, fn) {
        this._reconnectingWebsocket.addWebSocketListener(type, fn);
    }
    removeListener(type, fn) {
        this._reconnectingWebsocket.removeWebSocketListener(type, fn);
    }
    _updateConnState(connState) {
        this._connectionState = connState;
        this.connStateObserver().emit(interfaces_1.ConnectionStateChangeEvent, connState);
    }
    _setupWsListeners() {
        const { addWebSocketListener } = this._reconnectingWebsocket;
        this.addListener('open', (evt) => {
            this._updateConnState(interfaces_1.WsConnectionState.Connected);
            this._pendingMessageTracker.forEachUnexpired((data) => {
                this._sendMessage(data, { ttlIfDisconnected: 0 });
            });
        });
        this.addListener('close', (evt) => {
            // note: both error and close events will trigger, error event first I think
            this._updateConnState(interfaces_1.WsConnectionState.Disconnected);
        });
        this.addListener('error', (evt) => {
            this._updateConnState(interfaces_1.WsConnectionState.Disconnected);
        });
        if (this._onMessage) {
            this.addListener('message', (evt) => {
                try {
                    const data = JSON.parse(evt.data);
                    this._onMessage(data);
                }
                catch (err) {
                    console.error('Received invalid command over websockets', err, evt.data);
                }
            });
        }
    }
}
exports.WsReconnectingClient = WsReconnectingClient;
// WARNING: values currently in two spots and MUST CORRESPOND. See ws-topic-server.ts (in ws-server package) TODO: where to put this?
var InboundTopicsCommand;
(function (InboundTopicsCommand) {
    InboundTopicsCommand["JoinTopics"] = "JoinTopics";
    InboundTopicsCommand["LeaveTopics"] = "LeaveTopics";
})(InboundTopicsCommand || (InboundTopicsCommand = {}));
//# sourceMappingURL=ws-reconnecting-client.js.map